import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Hammer } from "lucide-react";
import * as Icons from "lucide-react";
import { Seo } from "@/components/Seo";
import { TrustBar } from "@/components/TrustBar";
import { GoogleBusinessSection } from "@/components/GoogleBusinessSection";
import { Section, SectionHeader } from "@/components/Section";
import { SERVICES, GALLERY, PROCESS, COMPANY } from "@/lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBraXRjaGVuJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgyMjY5ODQzfDA&ixlib=rb-4.1.0&q=85";
const HERO_IMG_2 = "https://images.unsplash.com/photo-1696987007764-7f8b85dd3033?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Renovation & Construction in the Durham Region and the GTA"
        description="IVrenovation: tile, bathroom and kitchen renovations, painting, flooring, electrical, drywall and carpentry. Free quote — Durham Region & GTA."
        image={HERO_IMG}
      />
      {/* ============ HERO ============ */}
      <section className="relative bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-32 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="overline"
              data-testid="hero-overline"
            >
              Renovation · Construction · Craftsmanship
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display mt-5 text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-zinc-950"
              data-testid="hero-headline"
            >
              We build spaces<br />
              that <span className="text-orange-600">work</span> as<br />
              hard as you do.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-7 max-w-xl text-base md:text-lg text-zinc-600 leading-relaxed"
            >
              {COMPANY.name} delivers tile work, full bathroom and kitchen renovations,
              painting, flooring, electrical, drywall and carpentry — finished to the
              millimetre, on schedule, every time.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                className="bg-orange-600 hover:bg-orange-700 text-white px-7 py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 transition"
                data-testid="hero-cta-quote"
              >
                Request a Free Quote
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/gallery"
                className="border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white px-7 py-4 text-sm font-bold uppercase tracking-wider transition"
                data-testid="hero-cta-gallery"
              >
                View Our Work
              </Link>
            </motion.div>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="aspect-[3/4] overflow-hidden border border-zinc-200"
            >
              <img
                src={HERO_IMG}
                alt="Modern kitchen renovation"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hidden md:block absolute -bottom-10 -left-10 w-48 h-60 overflow-hidden border border-zinc-200 bg-white"
            >
              <img
                src={HERO_IMG_2}
                alt="Luxury bathroom tile"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <div className="hidden lg:flex absolute -top-4 right-0 items-center gap-2 bg-zinc-950 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">
              <ShieldCheck className="w-4 h-4 text-orange-500" /> Built with precision
            </div>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE TRUST ============ */}
      <TrustBar />

      <div className="bg-zinc-950 text-zinc-100 border-y border-zinc-800 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-6">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-12 px-6 shrink-0">
              {["Tile Work", "Bathroom Renos", "Kitchen Renos", "Painting", "Flooring", "Electrical", "Drywall", "Carpentry", "Tile Work", "Bathroom Renos", "Kitchen Renos", "Painting"].map((label, i) => (
                <div key={`${dup}-${i}`} className="flex items-center gap-12">
                  <span className="font-display text-2xl md:text-3xl tracking-tight">{label}</span>
                  <span className="text-orange-500">✦</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ============ SERVICES ============ */}
      <Section>
        <div className="grid grid-cols-12 gap-8 items-end mb-14">
          <div className="col-span-12 md:col-span-7">
            <SectionHeader
              overline="What we do"
              title="Eight crafts. One responsible team."
              description="From a single backsplash to a full home renovation, every trade is delivered in-house with the same standard of care."
            />
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-bold text-zinc-900 border-b-2 border-orange-600 pb-1 hover:text-orange-600 transition"
              data-testid="services-see-all"
            >
              See all services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
          {SERVICES.map((s, idx) => {
            const Icon = Icons[s.icon] || Hammer;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="group bg-white p-8 hover:bg-zinc-950 hover:text-white transition-colors duration-300"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="text-xs font-bold text-orange-600 group-hover:text-orange-500">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <Icon className="w-7 h-7 text-zinc-900 group-hover:text-orange-500 transition-colors" />
                </div>
                <h3 className="font-display text-xl md:text-2xl tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-zinc-600 group-hover:text-zinc-300 transition-colors leading-relaxed">
                  {s.summary}
                </p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* ============ FEATURED PROJECTS ============ */}
      <Section className="bg-zinc-50">
        <div className="grid grid-cols-12 gap-8 items-end mb-12">
          <div className="col-span-12 md:col-span-8">
            <SectionHeader
              overline="Recent work"
              title="Finished projects, photographed honestly."
              description="No filters. No staging tricks. Just real renovations completed by our crew."
            />
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-bold text-zinc-900 border-b-2 border-orange-600 pb-1"
              data-testid="home-gallery-link"
            >
              Full gallery <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {GALLERY.slice(0, 6).map((g, idx) => {
            const spans = [
              "col-span-12 md:col-span-7 aspect-[4/3]",
              "col-span-12 md:col-span-5 aspect-[4/3]",
              "col-span-6 md:col-span-4 aspect-square",
              "col-span-6 md:col-span-4 aspect-square",
              "col-span-12 md:col-span-4 aspect-square",
              "col-span-12 md:col-span-12 aspect-[16/7]",
            ];
            return (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className={`${spans[idx]} group relative overflow-hidden border border-zinc-200 bg-white`}
                data-testid={`home-gallery-item-${g.id}`}
              >
                <img
                  src={g.image}
                  alt={g.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute left-0 bottom-0 p-5 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="text-xs uppercase tracking-wider text-orange-400 font-bold">{g.category}</p>
                  <p className="font-display text-white text-xl md:text-2xl tracking-tight">{g.title}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* ============ PROCESS ============ */}
      <Section dark>
        <div className="grid grid-cols-12 gap-8 items-end mb-14">
          <div className="col-span-12 md:col-span-8">
            <p className="overline" style={{ color: "#fb923c" }}>The Process</p>
            <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-white">
              Four steps. Zero surprises.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10">
          {PROCESS.map((p) => (
            <div key={p.step} className="bg-graphite p-8" data-testid={`process-step-${p.step}`}>
              <p className="font-display text-5xl text-orange-500">{p.step}</p>
              <h3 className="font-display text-xl mt-6 text-white">{p.title}</h3>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ GOOGLE BUSINESS ============ */}
      <GoogleBusinessSection />

      {/* ============ CTA ============ */}
      <section className="bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Ready to start your<br />renovation?
            </h2>
            <p className="mt-5 max-w-xl text-white/85 text-lg">
              Free on-site consultation. Transparent fixed quote. Honest craftsmanship from start to finish.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/contact"
              className="bg-zinc-950 hover:bg-white hover:text-zinc-950 text-white px-8 py-5 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 transition"
              data-testid="cta-bottom-button"
            >
              Book a Consultation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
